import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  listingCardImageFields,
  listingCardImageVariantParams,
} from '../../util/listingImageVariants';
import { isErrorUserPendingApproval, isForbiddenError, storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { constructQueryParamName, isOriginInUse } from '../../util/search';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { parse } from '../../util/urlHelpers';
import { getReferralParams } from '../../util/webStorageHelpers';
import { LISTING_BADGE_LISTING_FIELDS } from '../../config/r2rListingBadges';
import {
  avatarImageFieldVariants,
  listingCardAuthorUserFields,
  listingCardSponsorHighlightFields,
} from '../../util/avatarImageQueryFields';
import { LISTING_CARD_IMAGE_LIMIT, LISTING_CARD_MEDIA_FIELDS } from '../../util/listingMedia';
import { fetchSellerByUsername } from '../../util/api';
import { queryListingsForSearch } from '../../util/queryListingsForSearch';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { applyListedFilterToSearchParams } from './DateListedFilter/dateListedFilterHelpers';
import { resolveSearchPageSize } from './SearchResultsPanel/searchPageSize';
import { emptySellerSearchResponse, resolveSellerSearchParams } from './sellerSearchParams';

// Server render cannot see the window, so the first request uses 24.
// Once the grid is measured, resolveSearchPageSize() returns a full-row page.

// Map view fetches all matching pickup listings (not just a single result page).
// We page through results at the API maximum and stop at a safety cap.
const MAP_RESULT_PAGE_SIZE = 100;
const MAP_MAX_PAGES = 5;

// Lightweight listing fields needed to render map markers + the marker info card.
const MAP_LISTING_FIELDS = [
  'title',
  'geolocation',
  'price',
  'deleted',
  'state',
  'publicData.listingType',
  'publicData.unitType',
  'publicData.cardStyle',
  'publicData.priceVariationsEnabled',
  'publicData.priceVariants',
  'publicData.pickupEnabled',
  ...LISTING_CARD_MEDIA_FIELDS,
];

// ================ Helper Functions ================ //

const resultIds = data => {
  const listings = data.data;
  return listings
    .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
    .map(l => l.id);
};

// A slower listings.query must not replace the page a newer search already wrote.
const isLatestSearchResponse = (state, action) =>
  state.latestSearchRequestId == null || action.meta.requestId === state.latestSearchRequestId;

// ================ Async Thunks ================ //

/////////////////////
// Search Listings //
/////////////////////
// Build the SDK query params (filters, categories, price/dates/stock/seats/sort, listing types)
// from the SearchPage search params. Shared by the paginated list query and the map dataset query.
const buildSearchQueryParams = (searchParams, config) => {
  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = (listingTypes, listingTypePathParam, isListingTypeVariant) => {
    return isListingTypeVariant
      ? {
          pub_listingType: listingTypePathParam,
        }
      : config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const constructCategoryPropertiesForAPI = (queryParamPrefix, categories, level, params) => {
    const levelKey = `${queryParamPrefix}${level}`;
    const levelValue =
      typeof params?.[levelKey] !== 'undefined' ? `${params?.[levelKey]}` : undefined;
    if (!levelValue) return {};

    // Multi-select: OR within a level, AND across levels. Validate each value against
    // the current level's category nodes, then recurse into the union of their children.
    const values = `${levelValue}`.split(',').filter(Boolean);
    const validParents = values.map(v => categories.find(cat => cat.id === v)).filter(Boolean);

    if (validParents.length === 0) return {};

    const validLevelValues = validParents.map(cat => cat.id).join(',');
    const childCategories = [];
    const seenChildIds = new Set();
    validParents.forEach(parent => {
      (parent.subcategories || []).forEach(sub => {
        if (!seenChildIds.has(sub.id)) {
          seenChildIds.add(sub.id);
          childCategories.push(sub);
        }
      });
    });

    const nestedMaybe =
      childCategories.length > 0
        ? constructCategoryPropertiesForAPI(queryParamPrefix, childCategories, level + 1, params)
        : {};

    return { [levelKey]: validLevelValues, ...nestedMaybe };
  };

  /**
   * Category filter params are prepared here. We omit invalid category names.
   * I.e. params that are not part of the currently configured category tree.
   *
   * @param {string} paramName - The name of the parameter to prepare.
   * @param {Object} params - The search params object.
   * @returns {Object} The prepared parameter object.
   */
  const prepareCategoryParams = (paramName, params) => {
    const categoryConfig = config.search.defaultFilters?.find(f => f.schemaType === 'category');
    const categories = config.categoryConfiguration.categories;
    const { key, scope } = categoryConfig || {};
    const categoryParamPrefix = constructQueryParamName(key, scope);
    return paramName.startsWith(categoryParamPrefix)
      ? constructCategoryPropertiesForAPI(categoryParamPrefix, categories, 1, params)
      : {};
  };

  const constructIntegerRangePropertyForAPI = (queryParamPrefix, params) => {
    const integerValue = params?.[queryParamPrefix];
    const [min, max] = integerValue ? integerValue.split(',') : [];
    // NOTE: long filter needs exclusive max value on API side
    const inclusiveMin = Number.parseInt(min, 10);
    const exclusiveMax = Number.parseInt(max, 10) + 1;

    // NOTE: currently we don't validate the range values against the integer range config,
    // but we might want to do that in the future.

    return Number.isInteger(inclusiveMin) && Number.isInteger(exclusiveMax)
      ? { [queryParamPrefix]: [inclusiveMin, exclusiveMax].join(',') }
      : {};
  };

  /**
   * Integer range filter values are converted to API params of type 'long'.
   *
   * The range end must be exclusive. E.g. 1000,2000 -> 1000,2001
   *
   * NOTE: currently we don't validate the range values against the integer range config,
   * but we might want to do that in the future.
   *
   * @param {string} paramName - The name of the parameter to prepare.
   * @param {Object} params - The search params object.
   * @returns {Object} The prepared parameter object.
   */
  const prepareIntegerRangeParam = (paramName, params) => {
    const integerRangeConfig = config.listing.listingFields?.find(f => f.schemaType === 'long');
    const { key, scope } = integerRangeConfig || {};
    const integerParamPrefix = constructQueryParamName(key, scope);
    return paramName.startsWith(integerParamPrefix)
      ? constructIntegerRangePropertyForAPI(integerParamPrefix, params)
      : {};
  };

  // This function goes through given params and if there's a specific handler for the parameter type,
  // it calls the handler to prepare the property for API.
  // Otherwise, it just passes the param through.
  const prepareAPIParams = (params, paramHandlers) => {
    const pickedKeys = Object.entries(params).reduce((picked, [k, v]) => {
      const preparedParams = paramHandlers.reduce((picked, fn) => {
        return { ...picked, ...fn(k, params) };
      }, {});

      // If the param is not handled by any of the handlers, we pass it through.
      const currentParam = Object.keys(preparedParams).length > 0 ? preparedParams : { [k]: v };

      return { ...picked, ...currentParam };
    }, {});

    return pickedKeys;
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = config.search.defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          // Availability can be time-full or time-partial.
          // However, due to prolonged time window, we need to use time-partial.
          availability: 'time-partial',
          // minDuration uses minutes
          minDuration,
        }
      : {};
  };

  const stockFilters = datesMaybe => {
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // If dates filter is not in use,
    //   1) Add minStock filter with default value (1)
    //   2) Add relaxed stockMode: "match-undefined"
    // The latter is used to filter out all the listings that explicitly are out of stock,
    // but keeps bookable and inquiry listings.
    return hasDatesFilterInUse ? {} : { minStock: 1, stockMode: 'match-undefined' };
  };

  const seatsSearchParams = (seats, datesMaybe) => {
    const seatsFilter = config.search.defaultFilters.find(f => f.key === 'seats');
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // Seats filter cannot be applied without dates
    return hasDatesFilterInUse && seatsFilter ? { seats } : {};
  };

  const sortSearchParams = (sortParam, hasKeywords) => {
    const sortConfig = config?.search?.sortConfig || {};
    // If no sort options are set, defaultSort will be undefined
    const defaultSort = sortConfig?.options?.[0]?.key;
    const relevanceEnabled = sortConfig.options?.some(
      option => option.key === sortConfig.relevanceKey
    );

    // User-specified sort takes priority
    if (sortParam !== undefined && sortParam !== sortConfig.relevanceKey) {
      return { sort: sortParam };
    }

    // No sort parameter needed when keyword search is used or sort config is inactive
    if (relevanceEnabled && (hasKeywords || !sortConfig.active)) {
      return {};
    }

    // Fall back to default sort
    return { sort: defaultSort };
  };

  const {
    perPage,
    price,
    dates,
    seats,
    sort,
    mapSearch,
    listingTypePathParam,
    isListingTypeVariant,
    seller: _sellerParam,
    ...restOfParams
  } = searchParams;
  // The params related to default filters are prepared one-by-one
  // We could consider moving them to the prepareAPIParams function too.
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const stockMaybe = stockFilters(datesMaybe);
  const seatsMaybe = seatsSearchParams(seats, datesMaybe);
  const sortMaybe = sortSearchParams(sort, searchParams?.keywords !== undefined);

  // Filter out potential referral data parameters so that they are not included in the API query
  const { userTypes = [] } = config.user;
  const validReferralSources = getReferralParams(userTypes);
  const apiParamsRaw = Object.fromEntries(
    Object.entries(restOfParams).filter(entry => {
      const [key, value] = entry;

      return !validReferralSources.includes(key);
    })
  );

  const params = {
    // The params that are related to listing fields and categories are prepared here.
    // We add handler functions that check category and integer range configurations.
    // - With category params, we essentially just omit invalid category names.
    //   I.e. params that are not part of the currently configured category tree.
    // - With integer range params, we prepare the property for API.
    //   I.e. the range end must be exclusive. E.g. 1000,2000 -> 1000,2001
    // Note: invalid independent search params are still passed through
    ...prepareAPIParams(apiParamsRaw, [prepareCategoryParams, prepareIntegerRangeParam]),
    // If the search page variant is of type /s/:listingType, this sets the pub_listingType
    // query parameter to the value of the listing type path parameter. The ordering matters here,
    // since this value overrides any possible pub_listingType value coming from query parameters
    // i.e. the previous row.
    //
    // Only one value is currently supported in pub_listingType – if you want to support e.g.
    // /s/:listingType?pub_listingType=[otherListingType] => pub_listingType=listingType,otherListingType,
    // you'll need to customize a logic that merges the query param and path param values.
    ...searchValidListingTypes(
      config.listing.listingTypes,
      listingTypePathParam,
      isListingTypeVariant
    ),
    ...priceMaybe,
    ...datesMaybe,
    ...stockMaybe,
    ...seatsMaybe,
    ...sortMaybe,
    perPage,
  };

  // URL `listed=7d` → Marketplace `pub_publishedAt` min range.
  return applyListedFilterToSearchParams(params);
};

// Exported for unit tests (Date listed → pub_publishedAt mapping).
export { buildSearchQueryParams };

const RESOLVE_MEMO_MS = 5000;
const resolveMemo = new Map();

const resolveSellerFromApi = async username => {
  const key = String(username || '').toLowerCase();
  const cached = resolveMemo.get(key);
  if (cached && Date.now() - cached.at < RESOLVE_MEMO_MS) {
    return cached.promise;
  }
  const promise = fetchSellerByUsername(username)
    .then(result => result?.seller || null)
    .catch(e => {
      resolveMemo.delete(key);
      throw e;
    });
  resolveMemo.set(key, { at: Date.now(), promise });
  return promise;
};

const resolveSellerForSearch = async (searchParams, rejectWithValue) => {
  try {
    return await resolveSellerSearchParams(searchParams, resolveSellerFromApi);
  } catch (e) {
    return { reject: rejectWithValue(storableError(e)) };
  }
};

const searchListingsPayloadCreator = async ({ searchParams, config }, thunkAPI) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
  const resolved = await resolveSellerForSearch(searchParams, rejectWithValue);
  if (resolved.reject) {
    return resolved.reject;
  }

  if (resolved.skipQuery) {
    return {
      ...emptySellerSearchResponse(searchParams?.perPage),
      activeSeller: null,
      sellerNotFound: true,
    };
  }

  const params = buildSearchQueryParams(resolved.searchParams, config);

  return queryListingsForSearch(sdk, params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return {
        ...response,
        activeSeller: resolved.activeSeller,
        sellerNotFound: false,
      };
    })
    .catch(e => {
      const error = storableError(e);
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error);
    });
};

export const searchListings = createAsyncThunk(
  'SearchPage/searchListings',
  searchListingsPayloadCreator
);

// Map dataset query: fetch ALL pickup-enabled listings matching the current filters (up to a cap),
// looping pages so the map can plot every match rather than only the current result page.
const searchMapListingsPayloadCreator = async ({ searchParams, config }, thunkAPI) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
  const resolved = await resolveSellerForSearch(searchParams, rejectWithValue);
  if (resolved.reject) {
    return resolved.reject;
  }

  if (resolved.skipQuery) {
    return { ids: [] };
  }

  const baseParams = buildSearchQueryParams(resolved.searchParams, config);
  const listingFields = config?.listing?.listingFields;
  const sanitizeConfig = { listingFields };

  try {
    const firstResponse = await queryListingsForSearch(sdk, { ...baseParams, page: 1 });
    dispatch(addMarketplaceEntities(firstResponse, sanitizeConfig));

    const totalPages = firstResponse.data.meta?.totalPages || 1;
    const lastPage = Math.min(totalPages, MAP_MAX_PAGES);

    const remainingPageNumbers = [];
    for (let p = 2; p <= lastPage; p += 1) {
      remainingPageNumbers.push(p);
    }

    const remainingResponses = await Promise.all(
      remainingPageNumbers.map(p => queryListingsForSearch(sdk, { ...baseParams, page: p }))
    );
    remainingResponses.forEach(response => {
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
    });

    const ids = [firstResponse, ...remainingResponses].reduce(
      (acc, response) => acc.concat(resultIds(response.data)),
      []
    );
    return { ids };
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const searchMapListings = createAsyncThunk(
  'SearchPage/searchMapListings',
  searchMapListingsPayloadCreator
);

// ================ Slice ================ //

const searchPageSlice = createSlice({
  name: 'SearchPage',
  initialState: {
    pagination: null,
    searchParams: null,
    searchInProgress: false,
    searchListingsError: null,
    latestSearchRequestId: null,
    currentPageResultIds: [],
    activeListingId: null,
    activeSeller: null,
    sellerNotFound: false,
    // Map view dataset (all pickup-enabled matches, not just the current page)
    mapListingIds: [],
    mapListingsInProgress: false,
    mapListingsError: null,
  },
  reducers: {
    setActiveListing: (state, action) => {
      state.activeListingId = action.payload;
    },
  },
  extraReducers: builder => {
    // Search Listings
    builder
      .addCase(searchListings.pending, (state, action) => {
        state.latestSearchRequestId = action.meta.requestId;
        state.searchParams = action.meta.arg.searchParams;
        state.searchInProgress = true;
        state.searchListingsError = null;
        state.sellerNotFound = false;
      })
      .addCase(searchListings.fulfilled, (state, action) => {
        if (!isLatestSearchResponse(state, action)) {
          return;
        }
        state.currentPageResultIds = resultIds(action.payload.data);
        state.pagination = action.payload.data.meta;
        state.searchInProgress = false;
        state.activeSeller = action.payload.activeSeller || null;
        state.sellerNotFound = !!action.payload.sellerNotFound;
      })
      .addCase(searchListings.rejected, (state, action) => {
        if (!isLatestSearchResponse(state, action)) {
          return;
        }
        console.error(action.payload);
        state.searchInProgress = false;
        state.searchListingsError = action.payload;
        state.activeSeller = null;
        state.sellerNotFound = false;
      });

    // Search Map Listings (map view dataset)
    builder
      .addCase(searchMapListings.pending, state => {
        state.mapListingsInProgress = true;
        state.mapListingsError = null;
      })
      .addCase(searchMapListings.fulfilled, (state, action) => {
        state.mapListingIds = action.payload.ids;
        state.mapListingsInProgress = false;
      })
      .addCase(searchMapListings.rejected, (state, action) => {
        console.error(action.payload);
        state.mapListingsInProgress = false;
        state.mapListingsError = action.payload;
      });
  },
});

// Export the action creator
export const { setActiveListing } = searchPageSlice.actions;

export default searchPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const { listingType: listingTypePathParam } = params || {};
  const state = getState();
  const currentUser = state.user?.currentUser;
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const hasViewingRights = currentUser && hasPermissionToViewData(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData =
    !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized && hasViewingRights);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // `view`, `mobilemenu`, and `mobilesearch` are UI-only URL flags — never send to API.
  const { page = 1, address, origin, view, mobilemenu, mobilesearch, ...rest } = queryParams;
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  // In map view, restrict the (left) paginated list to pickup-enabled listings so the list and
  // the map stay in sync. `view` itself is a UI-only param and must not be sent to the API.
  const pickupOnlyMaybe = view === 'map' ? { pub_pickupEnabled: true } : {};

  const listingTypeVariantMaybe = listingTypePathParam
    ? { listingTypePathParam, isListingTypeVariant: true }
    : {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const searchListingsCall = searchListings({
    searchParams: {
      ...rest,
      ...pickupOnlyMaybe,
      ...originMaybe,
      ...listingTypeVariantMaybe,
      page,
      perPage: resolveSearchPageSize(),
      include: ['author', 'author.profileImage', 'images'],
      'fields.listing': [
        ...new Set([
          'title',
          'geolocation',
          'price',
          'deleted',
          'state',
          'publicData.listingType',
          'publicData.categoryLevel1',
          'publicData.categoryLevel2',
          'publicData.categoryLevel3',
          'publicData.transactionProcessAlias',
          'publicData.unitType',
          'publicData.cardStyle',
          // These help rendering of 'purchase' listings,
          // when transitioning from search page to listing page
          'publicData.pickupEnabled',
          'publicData.shippingEnabled',
          'publicData.sellerHasReviews',
          'publicData.priceVariationsEnabled',
          'publicData.priceVariants',
          ...listingCardSponsorHighlightFields,
          // publicData fields required by R2R listing badges (e.g. WYSIWYG)
          ...LISTING_BADGE_LISTING_FIELDS,
          ...LISTING_CARD_MEDIA_FIELDS,
        ]),
      ],
      'fields.user': listingCardAuthorUserFields,
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        ...listingCardImageFields(variantPrefix),
        ...avatarImageFieldVariants,
      ],
      ...listingCardImageVariantParams(variantPrefix, aspectRatio),
      'limit.images': LISTING_CARD_IMAGE_LIMIT,
    },
    config,
  });

  return dispatch(searchListingsCall);
};

// Client-side fetch of the map dataset (all pickup-enabled matches for the current filters).
// This is dispatched when the user switches to map view and whenever the active filters change.
// It is intentionally not part of `loadData` (SSR) since pulling hundreds of listings on every
// page load would be wasteful and the map only renders client-side.
export const searchMapListingsForView = (search, listingTypePathParam, config) => dispatch => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // Drop UI-only / pagination params; the map shows all matches across pages.
  const { page, address, origin, view, ...rest } = queryParams;

  const listingTypeVariantMaybe = listingTypePathParam
    ? { listingTypePathParam, isListingTypeVariant: true }
    : {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return dispatch(
    searchMapListings({
      searchParams: {
        ...rest,
        // Hard-restrict the map to pickup-enabled listings.
        pub_pickupEnabled: true,
        ...listingTypeVariantMaybe,
        perPage: MAP_RESULT_PAGE_SIZE,
        include: ['author', 'author.profileImage', 'images'],
        'fields.listing': MAP_LISTING_FIELDS,
        'fields.user': listingCardAuthorUserFields,
        'fields.image': [
          'variants.scaled-small',
          ...listingCardImageFields(variantPrefix),
          ...avatarImageFieldVariants,
        ],
        ...listingCardImageVariantParams(variantPrefix, aspectRatio),
        'limit.images': LISTING_CARD_IMAGE_LIMIT,
      },
      config,
    })
  );
};
